import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, Input, LoadingState, PageHeader, Text } from '@v2/design-system';
import { cn } from '@v2/utils/cn';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { hasAnyRole } from '@v2/lib/auth';
import { settingsInputClass } from '@v2/features/users/UserSettingsFields';
import { useI18n } from '@v2/providers/I18nProvider';

type StockReservationData = {
    ttl_hours: number;
    reminder_hours: number[];
    final_window_hours: number;
    final_interval_hours: number;
    notify_sales_managers_on_create: boolean;
    notify_commercial_director_on_create: boolean;
    notify_managers_on_reminder: boolean;
    notify_director_on_reminder: boolean;
    notify_managers_on_cancel: boolean;
    notify_director_on_cancel: boolean;
    notify_comex_on_writeoff: boolean;
    extra_emails: string[];
    active_reserved_count: number;
};

type FormState = {
    ttl_hours: string;
    reminder_hours: string;
    final_window_hours: string;
    final_interval_hours: string;
    notify_sales_managers_on_create: boolean;
    notify_commercial_director_on_create: boolean;
    notify_managers_on_reminder: boolean;
    notify_director_on_reminder: boolean;
    notify_managers_on_cancel: boolean;
    notify_director_on_cancel: boolean;
    notify_comex_on_writeoff: boolean;
    extra_emails: string;
};

const EMPTY_FORM: FormState = {
    ttl_hours: '48',
    reminder_hours: '72, 48, 24',
    final_window_hours: '8',
    final_interval_hours: '2',
    notify_sales_managers_on_create: true,
    notify_commercial_director_on_create: true,
    notify_managers_on_reminder: false,
    notify_director_on_reminder: false,
    notify_managers_on_cancel: true,
    notify_director_on_cancel: true,
    notify_comex_on_writeoff: true,
    extra_emails: '',
};

function parseCommaInts(value: string): number[] {
    return value
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((n) => Math.round(n));
}

function parseCommaEmails(value: string): string[] {
    return value
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
}

function dataToForm(data: StockReservationData): FormState {
    return {
        ttl_hours: String(data.ttl_hours),
        reminder_hours: (data.reminder_hours ?? []).join(', '),
        final_window_hours: String(data.final_window_hours),
        final_interval_hours: String(data.final_interval_hours),
        notify_sales_managers_on_create: data.notify_sales_managers_on_create,
        notify_commercial_director_on_create: data.notify_commercial_director_on_create,
        notify_managers_on_reminder: data.notify_managers_on_reminder,
        notify_director_on_reminder: data.notify_director_on_reminder,
        notify_managers_on_cancel: data.notify_managers_on_cancel,
        notify_director_on_cancel: data.notify_director_on_cancel,
        notify_comex_on_writeoff: data.notify_comex_on_writeoff,
        extra_emails: (data.extra_emails ?? []).join(', '),
    };
}

function formToPayload(form: FormState, applyTtlToExisting?: boolean) {
    const payload: Record<string, unknown> = {
        ttl_hours: Number(form.ttl_hours),
        reminder_hours: parseCommaInts(form.reminder_hours),
        final_window_hours: Number(form.final_window_hours),
        final_interval_hours: Number(form.final_interval_hours),
        notify_sales_managers_on_create: form.notify_sales_managers_on_create,
        notify_commercial_director_on_create: form.notify_commercial_director_on_create,
        notify_managers_on_reminder: form.notify_managers_on_reminder,
        notify_director_on_reminder: form.notify_director_on_reminder,
        notify_managers_on_cancel: form.notify_managers_on_cancel,
        notify_director_on_cancel: form.notify_director_on_cancel,
        notify_comex_on_writeoff: form.notify_comex_on_writeoff,
        extra_emails: parseCommaEmails(form.extra_emails),
    };

    if (applyTtlToExisting !== undefined) {
        payload.apply_ttl_to_existing = applyTtlToExisting;
    }

    return payload;
}



export default function StockReservationSettingsPage() {
    const { t } = useI18n();
    const qc = useQueryClient();
    const canAccess = hasAnyRole(['super_admin', 'admin']);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [loadedTtl, setLoadedTtl] = useState<number | null>(null);
    const [activeCount, setActiveCount] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    // Erro por campo (422): "extra_emails.1" → "extra_emails".
    const fieldError = (name: string) =>
        Object.entries(fieldErrors).find(([key]) => key === name || key.startsWith(`${name}.`))?.[1];

    const settingsQuery = useQuery({
        queryKey: ['settings', 'stock-reservation'],
        queryFn: async () =>
            (await api.get<{ data: StockReservationData }>('/settings/stock-reservation')).data.data,
        enabled: canAccess,
    });

    useEffect(() => {
        if (!settingsQuery.data) return;
        setForm(dataToForm(settingsQuery.data));
        setLoadedTtl(settingsQuery.data.ttl_hours);
        setActiveCount(settingsQuery.data.active_reserved_count ?? 0);
    }, [settingsQuery.data]);

    const saveMutation = useMutation({
        mutationFn: async (applyTtlToExisting?: boolean) => {
            const ttlChanged = loadedTtl != null && Number(form.ttl_hours) !== loadedTtl;
            const payload = formToPayload(form, ttlChanged ? applyTtlToExisting : undefined);
            return api.put('/settings/stock-reservation', payload);
        },
        onSuccess: (res) => {
            setFieldErrors({});
            toast.success(res.data?.message ?? t('v2.stock_reservation_settings.saved'));
            setConfirmOpen(false);
            void qc.invalidateQueries({ queryKey: ['settings', 'stock-reservation'] });
            void qc.invalidateQueries({ queryKey: ['meta', 'navigation'] });
        },
        onError: (err: { response?: { data?: { message?: string; active_reserved_count?: number; errors?: Record<string, string[]> } } }) => {
            if (typeof err?.response?.data?.active_reserved_count === 'number') {
                setActiveCount(err.response.data.active_reserved_count);
            }
            setFieldErrors(Object.fromEntries(Object.entries(err?.response?.data?.errors ?? {}).map(([k, v]) => [k, v[0] ?? ''])));
            setConfirmOpen(false);
            toast.error(err?.response?.data?.message ?? t('v2.stock_reservation_settings.save_error'));
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const ttlChanged = loadedTtl != null && Number(form.ttl_hours) !== loadedTtl;
        if (ttlChanged) {
            setConfirmOpen(true);
            return;
        }
        saveMutation.mutate(undefined);
    };

    if (!canAccess) {
        return <Navigate to="/" replace />;
    }

    if (settingsQuery.isLoading) {
        return <LoadingState variant="page" label={t('v2.common.loading')} />;
    }

    if (settingsQuery.isError) {
        const forbidden = (settingsQuery.error as { response?: { status?: number } } | null)?.response?.status === 403;
        return (
            <div>
                <PageHeader
                    title={t('v2.stock_reservation_settings.title')}
                    breadcrumb={[
                        { name: t('v2.nav.estoque'), href: '/estoque' },
                        { name: t('v2.stock_reservation_settings.title') },
                    ]}
                />
                <div className="flex flex-wrap items-center gap-3">
                    <p className="text-sm text-rose-600">
                        {forbidden ? t('v2.stock_reservation_settings.forbidden') : t('v2.stock_reservation_settings.load_error')}
                    </p>
                    {!forbidden && (
                        <Button size="sm" variant="outline" onClick={() => void settingsQuery.refetch()}>
                            {t('v2.stock_reservation_settings.retry')}
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    // Lembrete maior que o prazo nunca dispara (ex.: 72h com reserva de 48h).
    const ttl = Number(form.ttl_hours) || 0;
    const reminders = form.reminder_hours.split(',').map((v) => Number(v.trim())).filter((v) => Number.isFinite(v) && v > 0);
    const uselessReminders = ttl > 0 ? reminders.filter((h) => h >= ttl) : [];
    const finalWindow = Number(form.final_window_hours) || 0;
    const finalInterval = Number(form.final_interval_hours) || 0;
    const schedule = [...new Set([
        ...reminders.filter((h) => h < ttl),
        ...(finalWindow > 0 && finalInterval > 0
            ? Array.from({ length: Math.floor(finalWindow / finalInterval) }, (_, i) => finalWindow - i * finalInterval).filter((h) => h > 0 && h < ttl)
            : []),
    ])].sort((a, b) => b - a);

    return (
        <div>
            <PageHeader
                title={t('v2.stock_reservation_settings.title')}
                breadcrumb={[
                    { name: t('v2.nav.estoque'), href: '/estoque' },
                    { name: t('v2.stock_reservation_settings.title') },
                ]}
            />

            <form className="mt-4 space-y-6" onSubmit={handleSubmit}>
                <Text className="text-sm text-gray-600 dark:text-zinc-400">
                    {t('v2.stock_reservation_settings.section_help')}
                </Text>

                <div className="grid gap-6 xl:grid-cols-2">
                    <div className="space-y-5 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-900 dark:text-zinc-100">
                                {t('v2.stock_reservation_settings.ttl_hours')}
                            </label>
                            <Input
                                type="number"
                                min={1}
                                max={2160}
                                step={1}
                                className={settingsInputClass}
                                value={form.ttl_hours}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, ttl_hours: e.target.value }))
                                }
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                                {t('v2.stock_reservation_settings.ttl_hours_help')}
                                {ttl > 0 && ` (${t('v2.stock_reservation_settings.ttl_equals', { days: (ttl / 24).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) })})`}
                            </p>
                            <FieldError message={fieldError('ttl_hours')} />
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-900 dark:text-zinc-100">
                                {t('v2.stock_reservation_settings.reminder_hours')}
                            </label>
                            <Input
                                className={settingsInputClass}
                                value={form.reminder_hours}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, reminder_hours: e.target.value }))
                                }
                                placeholder={t('v2.stock_reservation_settings.reminder_hours_ph')}
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                                {t('v2.stock_reservation_settings.reminder_hours_help')}
                            </p>
                            {uselessReminders.length > 0 && (
                                <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                                    {t('v2.stock_reservation_settings.reminder_useless', { hours: uselessReminders.join(', '), ttl })}
                                </p>
                            )}
                            <FieldError message={fieldError('reminder_hours')} />
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-900 dark:text-zinc-100">
                                    {t('v2.stock_reservation_settings.final_window_hours')}
                                </label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={48}
                                    className={settingsInputClass}
                                    value={form.final_window_hours}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            final_window_hours: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-900 dark:text-zinc-100">
                                    {t('v2.stock_reservation_settings.final_interval_hours')}
                                </label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={12}
                                    className={settingsInputClass}
                                    value={form.final_interval_hours}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            final_interval_hours: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <p className="-mt-2 text-xs text-gray-500 dark:text-zinc-400">
                            {schedule.length > 0
                                ? t('v2.stock_reservation_settings.schedule_preview', { hours: schedule.map((h) => `${h}h`).join(', ') })
                                : t('v2.stock_reservation_settings.schedule_none')}
                        </p>
                        <FieldError message={fieldError('final_window_hours') ?? fieldError('final_interval_hours')} />

                    </div>

                    {/* Destinatários dos avisos de reserva saíram daqui: um lugar só para todos os e-mails do Hub. */}
                    <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">{t('v2.stock_reservation_settings.notify_section')}</p>
                        <p className="text-xs text-gray-500 dark:text-zinc-400">
                            Quem recebe os e-mails de reserva criada, lembrete, cancelamento, baixa e venda ganha agora é configurado em um lugar só, junto com os outros avisos do Hub.
                        </p>
                        <Link to="/settings/email-notifications" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                            Configurações → Notificações por e-mail →
                        </Link>
                    </div>
                </div>

                <Button
                    type="submit"
                    disabled={saveMutation.isPending}
                    isLoading={saveMutation.isPending && !confirmOpen}
                >
                    {t('common.save')}
                </Button>
            </form>

            <V2Modal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title={t('v2.stock_reservation_settings.ttl_confirm_title')}
                description={t('v2.stock_reservation_settings.ttl_confirm_body', {
                    count: String(activeCount),
                })}
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                            {t('v2.common.cancel')}
                        </Button>
                        <Button
                            variant="outline"
                            isLoading={saveMutation.isPending}
                            onClick={() => saveMutation.mutate(false)}
                        >
                            {t('v2.stock_reservation_settings.ttl_apply_new_only')}
                        </Button>
                        <Button
                            isLoading={saveMutation.isPending}
                            onClick={() => saveMutation.mutate(true)}
                        >
                            {t('v2.stock_reservation_settings.ttl_apply_existing', {
                                count: String(activeCount),
                            })}
                        </Button>
                    </>
                }
            >
                <Text className="text-sm text-gray-600 dark:text-zinc-400">
                    {t('v2.stock_reservation_settings.ttl_confirm_help')}
                </Text>
            </V2Modal>
        </div>
    );
}

function FieldError({ message }: { message?: string }) {
    return message ? <p className="mt-1 text-xs font-medium text-rose-600 dark:text-rose-400">{message}</p> : null;
}
